import { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { Bell, BookOpen, Award, TrendingUp, CheckCircle, X, Flame, Loader2 } from 'lucide-react';
import { Button } from './button';
import { ScrollArea } from './scroll-area';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const NotificationPanel = ({ isOpen, onClose }) => {
    const { language, getLocalizedField } = useLanguage();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    const fetchNotifications = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API}/notifications?limit=20`);
            setNotifications(response.data);
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const markAsRead = async (id) => {
        try {
            await axios.put(`${API}/notifications/${id}/read`);
            setNotifications(prev => 
                prev.map(n => n.id === id ? {...n, read: true} : n)
            );
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const markAllRead = async () => {
        try {
            await axios.put(`${API}/notifications/mark-all-read`);
            setNotifications(prev => prev.map(n => ({...n, read: true})));
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };

    const getIcon = (type) => {
        const icons = {
            lesson: BookOpen,
            badge: Award,
            insight: TrendingUp,
            complete: CheckCircle,
            streak: Flame
        };
        const Icon = icons[type] || Bell;
        return <Icon className="w-4 h-4" />;
    };

    const getIconColor = (type) => {
        const colors = {
            lesson: 'bg-blue-50 text-blue-600',
            badge: 'bg-amber-50 text-amber-600',
            insight: 'bg-emerald-50 text-emerald-600',
            complete: 'bg-primary/10 text-primary',
            streak: 'bg-orange-50 text-orange-600'
        };
        return colors[type] || 'bg-slate-50 text-slate-600';
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return language === 'id' ? 'Baru saja' : 'Just now';
        if (diffMins < 60) return `${diffMins} ${language === 'id' ? 'menit lalu' : 'min ago'}`;
        if (diffHours < 24) return `${diffHours} ${language === 'id' ? 'jam lalu' : 'hours ago'}`;
        return `${diffDays} ${language === 'id' ? 'hari lalu' : 'days ago'}`;
    };

    if (!isOpen) return null;

    return (
        <div className="absolute right-0 top-full mt-2 w-80 z-50 notification-panel" data-testid="notification-panel">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <h3 className="font-heading font-semibold text-slate-900">
                    {language === 'id' ? 'Notifikasi' : 'Notifications'}
                </h3>
                <div className="flex items-center gap-2">
                    {notifications.some(n => !n.read) && (
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-7 text-xs text-primary"
                            onClick={markAllRead}
                        >
                            {language === 'id' ? 'Tandai semua' : 'Mark all'}
                        </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
                        <X className="w-4 h-4" />
                    </Button>
                </div>
            </div>
            <ScrollArea className="max-h-80">
                {loading ? (
                    <div className="p-8 text-center">
                        <Loader2 className="w-6 h-6 text-slate-300 mx-auto animate-spin" />
                    </div>
                ) : notifications.length === 0 ? (
                    <div className="p-8 text-center">
                        <Bell className="w-10 h-10 text-slate-200 mx-auto mb-3" />
                        <p className="text-sm text-slate-400">
                            {language === 'id' ? 'Tidak ada notifikasi' : 'No notifications'}
                        </p>
                    </div>
                ) : (
                    notifications.map((notification) => (
                        <div 
                            key={notification.id} 
                            className={`notification-item cursor-pointer ${!notification.read ? 'bg-primary/5' : ''}`}
                            onClick={() => !notification.read && markAsRead(notification.id)}
                        >
                            {!notification.read && <div className="notification-dot" />}
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${getIconColor(notification.notification_type)}`}>
                                {getIcon(notification.notification_type)}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 line-clamp-2">
                                    {language === 'en' && notification.title_en 
                                        ? notification.title_en 
                                        : notification.title
                                    }
                                </p>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {formatTime(notification.created_at)}
                                </p>
                            </div>
                        </div>
                    ))
                )}
            </ScrollArea>
        </div>
    );
};
